import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import "c3/c3.min.css";
import * as d3 from "d3";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import {
  XRangeValue,
  C3ChartID,
  SVGSelecter,
  getRootElement,
  insertRangeElement,
  getRangeElement,
  initRangeElement,
  displayChartPoint,
  getAxisXValues,
  startRangeSelection,
  setRangeSelection,
  insertZoomTarget,
  initZoomTarget,
  AxisRange,
  setMousewheel,
  setXAxisRange,
  setYAxisRange,
  roundYAxisValue,
  setZoomTarget,
  ChartDataArray,
  transpose,
  ChartSize,
  resizeChart,
} from "../common/chart";
import ChartDialog from "../common/ChartDialog";
import { AnyType, ChartDialogConfig } from "../../../../../common/constants";
import { ChartRowDataArray } from "../../../../../webapi/constants";
import { IProfileRow } from "../../../../../webapi/service/getProfile";
import "../../../../../App.css";

type CircleNode = AnyType;
type CircleData = AnyType;
type CircleValue = AnyType;

const LowlimitLabel = "Low limit";
const ReferenceLabel = "Reference";
const HighlimitLabel = "High limit";

/**
 * チャートの点ノードリストを取得します
 * @param c3i - C3
 * @returns 点ノードリスト
 */
function getCircles(c3i: C3.ChartInternal): NodeList {
  return c3i.getCircles()._groups[0];
}

/**
 * 入れ子の点データを取得します
 * @param data - 点データ
 * @param x - 点X
 * @returns 入れ子データ
 */
function getNestData(data: CircleData, x: number | undefined): CircleData {
  if (typeof x !== "number") {
    return data;
  }
  return data.values.find((value: CircleData) => {
    return value.x === x;
  });
}

/**
 * 点データを取得します
 * @param circle - 点ノード
 * @returns 点データ
 */
function getCircleData(circle: CircleNode): CircleData {
  const data = circle.__data__;
  if (typeof data.x !== "undefined") {
    return data;
  }
  const prev = circle.previousElementSibling;
  const next = circle.nextElementSibling;
  let x: number | undefined = undefined;
  if (prev && next) {
    x = (prev.__data__.x + next.__data__.x) / 2;
  } else if (prev) {
    x = prev.__data__.x + 1;
  } else if (next) {
    x = next.__data__.x - 1;
  }
  return getNestData(data, x);
}

/**
 * マウスで選択した点データを取得します
 * @param circles - 点ノード配列
 * @param mouseX - マウスX座標
 * @param mouseY - マウスY座標
 * @returns 点データ
 */
function getCircle(
  circles: CircleNode[],
  mouseX: number,
  mouseY: number
): CircleData {
  let minSqrt = Infinity;
  let selectCircle: CircleData = undefined;
  circles.forEach((circle: CircleNode) => {
    const select = d3.select(circle);
    const cx = parseInt(select.attr("cx"));
    const cy = parseFloat(select.attr("cy"));
    const sqrt = Math.sqrt(Math.pow(cx - mouseX, 2) + Math.pow(cy - mouseY, 2));
    if (sqrt < 8 && sqrt < minSqrt) {
      minSqrt = sqrt;
      selectCircle = getCircleData(circle);
    }
  });
  return selectCircle;
}

/**
 * Y軸範囲
 */
interface IYRange {
  /**
   * X軸座標
   */
  x: number;
  /**
   * Y軸最小値
   */
  min: CircleValue;
  /**
   * Y軸最大値
   */
  max: CircleValue;
}

/**
 * Y軸範囲を取得します
 * @param dataAry - C3データ
 * @param selectData - 選択中C3データ
 * @param lowMin - 最小値
 * @param highMax - 最大値
 * @returns Y軸範囲
 */
function getYRange(
  dataAry: C3.DataPoint[],
  selectData: C3.DataPoint,
  lowMin: number | undefined,
  highMax: number | undefined
): IYRange {
  let min: CircleValue = undefined;
  let max: CircleValue = undefined;
  const selectID = selectData.id;
  const selectX = selectData.x;
  const selectValue = selectData.value;
  switch (selectID) {
    case LowlimitLabel:
      max = dataAry
        .filter((data) => {
          const { id, x, value } = data;
          return id === ReferenceLabel && x === selectX && selectValue <= value;
        })
        .map((data) => {
          return data.value;
        })
        .reduce((v1, v2) => {
          return Math.max(v1, v2);
        });
      min = lowMin;
      break;
    case ReferenceLabel:
      dataAry
        .filter((data) => {
          const { id, x } = data;
          return id !== selectID && x === selectX;
        })
        .forEach((data) => {
          const { id, value } = data;
          if (id === LowlimitLabel && value <= selectValue) {
            if (!min || (min && value > min)) {
              min = value;
            }
          }
          if (id === HighlimitLabel && selectValue <= value) {
            if (!max || (max && max > value)) {
              max = value;
            }
          }
        });
      break;
    case HighlimitLabel:
      max = highMax;
      min = dataAry
        .filter((data) => {
          const { id, x, value } = data;
          return id === ReferenceLabel && x === selectX && value <= selectValue;
        })
        .map((data) => {
          return data.value;
        })
        .reduce((v1, v2) => {
          return Math.min(v1, v2);
        });
      break;
  }
  return { x: selectX, min: min, max: max };
}

/**
 * C3の設定値を配列で取得します
 * @param c3 - C3
 * @returns C3設定値配列
 */
function getValues(c3: C3.ChartAPI) {
  return c3
    .data()
    .map((d) => {
      return d.values;
    })
    .flat();
}

/**
 * Y軸範囲を配列で取得します
 * @param c3 - C3
 * @param startX - X軸開始位置
 * @param endX - X軸終了位置
 * @param dataID - データID
 * @param lowMin - Y軸最小値
 * @param highMax - Y軸最大値
 * @returns Y軸範囲の配列
 */
function getYRanges(
  c3: C3.ChartAPI,
  startX: number,
  endX: number,
  dataID: string,
  lowMin: number | undefined,
  highMax: number | undefined
): IYRange[] {
  const dataAry = getValues(c3).filter((data) => {
    return startX <= data.x && data.x <= endX;
  });
  return dataAry
    .filter((data) => {
      return data.id === dataID;
    })
    .map((data) => {
      return getYRange(dataAry, data, lowMin, highMax);
    });
}

/**
 * 点のY軸に設定している値を取得します
 * @param circles - 点ノードリスト
 * @param id - データID
 * @param x - X軸座標
 * @returns Y軸設定値
 */
function getValue(circles: NodeList, id: string, x: CircleValue): CircleValue {
  let value: CircleValue;
  circles.forEach((circle: CircleNode) => {
    let data = circle.__data__;
    if (typeof data.x === "undefined") {
      data = getNestData(data, x);
    }
    if (data.x === x && data.id === id) {
      value = data.value;
    }
  });
  return value;
}

/**
 * Y軸の値を取得します
 * @param value - Y軸値
 * @param range - Y軸範囲
 * @returns - Y軸値
 */
function getYValue(value: CircleValue, range: IYRange | undefined) {
  if (range) {
    const min = range.min;
    if (typeof min !== "undefined" && value < min) {
      value = min;
    }
    const max = range.max;
    if (typeof max !== "undefined" && value > max) {
      value = max;
    }
  }
  return roundYAxisValue(value);
}

/**
 * 点データを更新します
 * @param c3i - C3
 * @param circles - 点ノードリスト
 * @param circleDataID - データID
 * @param xrange - X軸制限範囲
 * @param yrange - Y軸制限範囲
 * @param ranges - Y軸範囲の配列
 * @param amount - Y軸移動量
 */
function updateCirclesValue(
  c3i: C3.ChartInternal,
  circles: NodeList,
  circleDataID: string,
  xrange: { start: XRangeValue; end: XRangeValue },
  yrange: { min: number; max: number },
  ranges: IYRange[],
  amount: number
) {
  const { start, end } = xrange;
  const { min, max } = yrange;
  const scale = c3i.y;
  const yValue = scale.invert;
  circles.forEach((circle: CircleNode) => {
    const data = getCircleData(circle);
    const { id, x, value } = data;
    if (
      circleDataID === id &&
      start <= x &&
      x <= end &&
      min <= value &&
      value <= max
    ) {
      const range = ranges.find((range) => {
        return range.x === x;
      });
      const y = scale(value) + amount;
      data.value = getYValue(yValue(y), range);
    }
  });
}

/**
 * 点データを更新します
 * @param circles - 点ノードリスト
 * @param circleDataID - データID
 * @param xrange - X軸制限範囲
 * @param ranges - Y軸範囲の配列
 * @param value - Y軸設定値
 */
function updateCirclesDirectValue(
  circles: NodeList,
  circleDataID: string,
  xrange: { start: number; end: number },
  ranges: IYRange[],
  value: number
) {
  const { start, end } = xrange;
  circles.forEach((circle: CircleNode) => {
    const data = getCircleData(circle);
    const { id, x } = data;
    if (circleDataID === id && start <= x && x <= end) {
      const range = ranges.find((range) => {
        return range.x === x;
      });
      data.value = getYValue(value, range);
    }
  });
}

/**
 * C3に設定するY軸範囲を取得します
 * @param values - チャートのY軸値配列
 * @param correction - Y軸拡大補正値
 * @param zeroValue - Y軸最大最小が0の場合、Y軸に最大最小に設定する値
 * @returns Y軸範囲
 */
function getYAxisRange(
  values: number[],
  correction: number,
  zeroValue: number
): AxisRange {
  let min = values.reduce((v1, v2) => {
    return Math.min(v1, v2);
  });
  let max = values.reduce((v1, v2) => {
    return Math.max(v1, v2);
  });
  if (min === 0 && max === 0) {
    min = zeroValue * -1;
    max = zeroValue;
  } else if (min === 0) {
    min = Math.abs(max) * -1;
  } else if (max === 0) {
    max = Math.abs(min);
  }
  let minCorrection = min * correction;
  let maxCorrection = max * correction;
  let centerCorrection = (Math.abs(minCorrection) + Math.abs(maxCorrection)) / 2;
  if (minCorrection !== maxCorrection) {
    if (minCorrection > 0 && maxCorrection > 0) {
      centerCorrection = (Math.abs(maxCorrection) - Math.abs(minCorrection)) / 2;
    } else if (minCorrection < 0 && maxCorrection < 0) {
      centerCorrection = (Math.abs(minCorrection) - Math.abs(maxCorrection)) / 2;
    }
  }
  minCorrection = minCorrection > 0 ? centerCorrection : centerCorrection * -1;
  maxCorrection = maxCorrection > 0 ? centerCorrection : centerCorrection * -1;
  min = min > 0 ? min - minCorrection : min + minCorrection;
  max = max < 0 ? max - maxCorrection : max + maxCorrection;
  return { min: min, max: max };
}

/**
 * 非表示のデータIDを取得します
 * @param c3i - C3
 * @returns 非表示データIDリスト
 */
function getHiddenIds(c3i: C3.ChartInternal): string[] {
  return c3i.legend.selectAll("g.c3-legend-item.c3-legend-item-hidden").data();
}

export type EditChartProfile = IProfileRow | undefined;

/**
 * マウスドラッグ情報
 */
type DragInfo = {
  /**
   * ドラッグ中の場合trueを設定します
   */
  drag: boolean;
  /**
   * 選択中点データ
   */
  circle?: CircleData;
  /**
   * X軸開始位置
   */
  start?: XRangeValue;
  /**
   * X軸終了位置
   */
  end?: XRangeValue;
  /**
   * Y軸範囲の配列
   */
  ranges?: IYRange[];
};

/**
 * マウスクリック位置。
 * Zoomターゲットに使用
 */
type ClickPos = {
  /**
   * X座標
   */
  x: number;
  /**
   * Y座標
   */
  y: number;
};

export interface IProps extends WithTranslation {
  /**
   * チャートダイアログを表示する場合はtrue
   */
  show: boolean;
  /**
   * チャート幅
   */
  width: number;
  /**
   * チャート高さ
   */
  height: number;
  /**
   * プロファイルデータ
   */
  profile: EditChartProfile;
  /**
   * プロファイル編集データを保存する場合に呼び出します
   */
  onOk: (datas: ChartRowDataArray) => void;
  /**
   * チャートダイアログを閉じる場合に呼び出します
   */
  onCancel: () => void;
  /**
   * 確認ダイアログを表示する場合に呼び出します
   */
  showConfirm: (params: IConfirmDialogParams) => void;
}

interface IState {
  /**
   * 編集モードにする場合はtrue
   */
  edit: boolean;
  /**
   * プロファイル編集メニューの選択項目
   */
  items: { text: string; value: string }[];
  /**
   * プロファイル編集メニューのX軸開始位置
   */
  start: number | undefined;
  /**
   * プロファイル編集メニューのX軸終了位置
   */
  end: number | undefined;
  /**
   * チャートのX軸制限値
   */
  xAxisRange: AxisRange;
  /**
   * チャートのY軸制限値
   */
  yAxisRange: AxisRange;
}

class EditChartDialog extends React.Component<IProps, IState> {
  private readonly ChartID = "chart_edit";
  private readonly C3ChartID = C3ChartID(this.ChartID);
  private readonly SvcTag = SVGSelecter(this.C3ChartID);
  private readonly YAxisRangeCorrection =
    ChartDialogConfig.CHART_Y_AXIS_RANGE_CORRECTION;
  private readonly YAxisZeroValue = ChartDialogConfig.CHART_Y_AXIS_ZERO_VALUE;
  private _c3: C3.ChartAPI | null = null;
  private _chartSize: ChartSize = { width: 0, height: 0 };
  private _dragInfo: DragInfo = { drag: false };
  private _contextmenuEnabled = true;
  private readonly XScaleRate = ChartDialogConfig.CHART_X_AXIS_SCALE_RATE;
  private _xZoomCount = 0;
  private readonly YScaleRate = ChartDialogConfig.CHART_Y_AXIS_SCALE_RATE;
  private _yZoomCount = 0;
  private _clickPos: ClickPos = { x: 0, y: 0 };

  constructor(props: IProps) {
    super(props);
    this.state = {
      edit: false,
      items: [],
      start: undefined,
      end: undefined,
      xAxisRange: { min: 0, max: 0 },
      yAxisRange: { min: 0, max: 0 },
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleXAxisZoomUp = this.handleXAxisZoomUp.bind(this);
    this.handleXAxisZoomOut = this.handleXAxisZoomOut.bind(this);
    this.handleYAxisZoomUp = this.handleYAxisZoomUp.bind(this);
    this.handleYAxisZoomOut = this.handleYAxisZoomOut.bind(this);
    this.handleChangeXRange = this.handleChangeXRange.bind(this);
    this.handleRegist = this.handleRegist.bind(this);
    this.handleChartMouseup = this.handleChartMouseup.bind(this);
    this.dragInfo = this.dragInfo.bind(this);
    this.clickStart = this.clickStart.bind(this);
    this.chartSize = this.chartSize.bind(this);
    this.setEditMenuXRange = this.setEditMenuXRange.bind(this);
  }

  componentDidUpdate(prevProps: IProps) {
    const { profile, width, height } = this.props;
    if (!prevProps.profile && profile) {
      console.time(profile.channel);
      this.drawChart(profile.data, this.C3ChartID, {
        width: width,
        height: height,
      });
      console.timeEnd(profile.channel);

      const items = profile.data[0]
        .filter((d) => {
          return d !== "x";
        })
        .map((d: string) => {
          const index = d.indexOf(" ");
          const text = index >= 0 ? d.substring(0, index) : d;
          return { text: text, value: d };
        });
      this.setState({ items: items.reverse() });

      this._contextmenuEnabled = true;
      this._xZoomCount = 0;
      this._yZoomCount = 0;
    }
  }

  /**
   * ダイアログクローズイベント
   */
  handleClose() {
    this.setState({ edit: false, start: undefined, end: undefined });
    this.props.onCancel();
  }

  /**
   * 編集ボタンイベント
   */
  handleEdit() {
    if (this._c3) {
      this._contextmenuEnabled = false;
      displayChartPoint(this._c3, true);
      this.editChart(this._c3, true);
      this.setState({ edit: true });
    }
  }

  /**
   * 保存ボタンイベント
   */
  handleSave() {
    if (this._c3) {
      const { showConfirm, onOk, t } = this.props;
      const datas = this._c3.data();
      showConfirm({
        title: t("profile_edit_save"),
        message: t("profile_edit_save_message"),
        onOk: () => {
          const datalist: ChartDataArray = Array(datas.length);
          datas.forEach((data, i) => {
            const { id, values } = data;
            datalist[i] = Array(values.length + 1);
            datalist[i][0] = id;
            values.forEach((value, j) => {
              datalist[i][j + 1] = value.value;
            });
          });
          this.setState({ edit: false });
          onOk(transpose(datalist));
        },
      });
    }
  }

  /**
   * X軸制限値プロパティ
   * @param range - X軸範囲
   * @returns X軸範囲
   */
  xAxisRange(range?: AxisRange): AxisRange {
    if (range) {
      this.setState({ xAxisRange: range });
    }
    return this.state.xAxisRange;
  }

  /**
   * Y軸制限値プロパティ
   * @param range - Y軸範囲
   * @returns Y軸範囲
   */
  yAxisRange(range?: AxisRange): AxisRange {
    if (range) {
      this.setState({ yAxisRange: range });
    }
    return this.state.yAxisRange;
  }

  /**
   * X軸拡大回数プロパティ
   * @param count - X軸拡大回数
   * @returns X軸拡大回数
   */
  xZoomCount(count?: number): number {
    if (typeof count !== "undefined") {
      this._xZoomCount = count;
    }
    return this._xZoomCount;
  }

  /**
   * Y軸拡大回数プロパティ
   * @param count - Y軸拡大回数
   * @returns Y軸拡大回数
   */
  yZoomCount(count?: number): number {
    if (typeof count !== "undefined") {
      this._yZoomCount = count;
    }
    return this._yZoomCount;
  }

  /**
   * X軸拡大率を取得します
   * @param count - X軸拡大回数
   * @returns X軸拡大率
   */
  getXRate(count: number): number {
    return count * this.XScaleRate;
  }

  /**
   * Y軸拡大率を取得します
   * @param count - Y軸拡大回数
   * @returns Y軸拡大率
   */
  getYRate(count: number): number {
    return count * this.YScaleRate;
  }

  /**
   * X軸拡大ボタンイベント
   */
  handleXAxisZoomUp() {
    const c3 = this._c3;
    if (c3) {
      const range = this.xAxisRange();
      const count = this.xZoomCount() + 1;
      const rate = this.getXRate(count);
      if (rate <= 1) {
        setXAxisRange(c3, rate >= 1 ? 0.95 : rate, range);
        this.xZoomCount(count);
      }
    }
  }

  /**
   * X軸縮小ボタンイベント
   */
  handleXAxisZoomOut() {
    const c3 = this._c3;
    if (c3) {
      const range = this.xAxisRange();
      const count = this.xZoomCount() - 1;
      const rate = this.getXRate(count);
      if (rate >= 0) {
        setXAxisRange(c3, rate, range);
        this.xZoomCount(count);
      }
    }
  }

  /**
   * Y軸拡大ボタンイベント
   */
  handleYAxisZoomUp() {
    const c3 = this._c3;
    if (c3) {
      const range = this.yAxisRange();
      const count = this.yZoomCount() + 1;
      const rate = this.getYRate(count);
      if (rate <= 1) {
        setYAxisRange(c3, rate >= 1 ? 0.95 : rate, range);
        this.yZoomCount(count);
      }
    }
  }

  /**
   * Y軸縮小ボタンイベント
   */
  handleYAxisZoomOut() {
    const c3 = this._c3;
    if (c3) {
      const range = this.yAxisRange();
      const count = this.yZoomCount() - 1;
      const rate = this.getYRate(count);
      if (rate >= 0) {
        setYAxisRange(c3, rate, range);
        this.yZoomCount(count);
      }
    }
  }

  /**
   * プロファイル編集メニューのX軸範囲変更イベント
   * @param start - プロファイル編集メニューのX軸開始位置
   * @param end - プロファイル編集メニューのX軸終了位置
   */
  handleChangeXRange(start: number | undefined, end: number | undefined) {
    if (this._c3) {
      let x = 0;
      let width = 0;
      let baseX = 0;
      let startX = 0;
      let endX = 0;
      if (typeof start !== "undefined" && typeof end !== "undefined") {
        if (start < end) {
          const c3i = this._c3.internal;
          const sx = c3i.x(start);
          const ex = c3i.x(end);
          x = sx;
          width = ex - sx;
          baseX = start;
          startX = start;
          endX = end;
        }
      }
      getRangeElement(this._c3)
        .attr("x", x)
        .attr("width", width)
        .attr("base", baseX)
        .attr("start", startX)
        .attr("end", endX);
    }
  }

  /**
   * Y軸制限値を更新します
   * @param c3 - C3
   * @param correction - Y軸拡大補正値
   * @param minHeight - Y軸最大最小が0の場合、Y軸に最大最小に設定する値
   * @returns Y軸制限値
   */
  updateYAxisRange(c3: C3.ChartAPI, correction: number, minHeight: number) {
    const values = c3
      .data()
      .map((d) => {
        return d.values;
      })
      .flat()
      .map((d2) => {
        return d2.value;
      });
    const { min, max } = getYAxisRange(
      Array.from(new Set(values)),
      correction,
      minHeight
    );
    const range = this.yAxisRange();
    const minY = min < range.min ? min : range.min;
    const maxY = max > range.max ? max : range.max;
    this.yAxisRange({ min: minY, max: maxY });
    return { min: minY, max: maxY };
  }

  /**
   * Y軸Zoomを更新します
   * @param c3 - C3
   * @param range - Y軸制限値
   */
  updateYAxisZoom(c3: C3.ChartAPI, range: AxisRange) {
    const count = this.yZoomCount();
    const rate = this.getYRate(count);
    if (0 <= rate && rate < 1) {
      setYAxisRange(c3, rate, range);
    } else if (rate >= 1) {
      setYAxisRange(c3, 0.95, range);
    }
  }

  /**
   * プロファイル編集メニューの登録ボタンイベント
   * @param id - プロファイル編集メニューの選択データID
   * @param start - プロファイル編集メニューのX軸開始位置
   * @param end - プロファイル編集メニューのX軸終了位置
   * @param value - プロファイル編集メニューのY軸設定値
   */
  handleRegist(
    id: string,
    start: number,
    end: number | undefined,
    value: number
  ) {
    if (this._c3) {
      const c3i = this._c3.internal;
      const xrange = this.xAxisRange();
      const startX = start;
      const endX = typeof end !== "undefined" ? end : startX;
      updateCirclesDirectValue(
        getCircles(c3i),
        id,
        {
          start: startX < xrange.min ? xrange.min : startX,
          end: xrange.max < endX ? xrange.max : endX,
        },
        getYRanges(this._c3, startX, endX, id, undefined, undefined),
        Number(value)
      );
      c3i.redraw({
        withY: false,
        withSubchart: false,
        withTransition: false,
        withTrimXDomain: false,
        withEventRect: false,
        withDimension: false,
      });
      this.updateYAxisZoom(
        this._c3,
        this.updateYAxisRange(
          this._c3,
          this.YAxisRangeCorrection,
          this.YAxisZeroValue
        )
      );
      this.setEditMenuXRange(startX, startX !== endX ? endX : undefined);
    }
  }

  /**
   * プロファイル編集メニューのX軸範囲を設定します
   * @param start - プロファイル編集メニューのX軸開始位置
   * @param end - プロファイル編集メニューのX軸終了位置
   */
  setEditMenuXRange(start: number | undefined, end?: number) {
    this.setState({ start: start, end: end });
  }

  /**
   * マウスドラッグプロパティ
   * @param dragInfo - マウスドラッグ情報
   * @returns マウスドラッグ情報
   */
  dragInfo(dragInfo?: DragInfo) {
    if (dragInfo) {
      this._dragInfo = dragInfo;
    }
    return this._dragInfo;
  }

  /**
   * マウスクリック位置プロパティ
   * @param clickPos - マウスクリック位置
   * @returns マウスクリック位置
   */
  clickStart(clickPos?: ClickPos) {
    if (clickPos) {
      this._clickPos = clickPos;
    }
    return this._clickPos;
  }

  /**
   * マウスUPイベント
   */
  handleChartMouseup() {
    this.dragInfo({ drag: false });
    window.removeEventListener("mouseup", this.handleChartMouseup);
  }

  /**
   * コンテキストメニューイベント追加
   * @param selecter コンテキストメニューイベント追加位置
   */
  addContextMenu(selecter: string): void {
    const element = document.querySelector(selecter);
    if (element) {
      element.addEventListener("contextmenu", (e) => {
        if (this._contextmenuEnabled === true) {
          return true;
        } else {
          e.preventDefault();
        }
      });
    }
  }

  /**
   * チャートサイズプロパティ
   * @param size - チャートサイズ
   * @returns チャートサイズ
   */
  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  /**
   * チャートX軸Y軸を初期化します
   * @param c3 - C3
   */
  initAxisRange(c3: C3.ChartAPI) {
    const { min, max } = c3.axis.range();
    this.xAxisRange({ min: min.x, max: max.x });
    this.yAxisRange({ min: min.y, max: max.y });
    this.xZoomCount(0);
    this.yZoomCount(0);
  }

  /**
   * チャートを描画します
   * @param rows - チャートデータ
   * @param chartid - チャートID
   * @param size - チャートサイズ
   */
  drawChart(rows: C3.PrimitiveArray[], chartid: string, size: ChartSize) {
    const datanum = rows.length - 1;
    const minX = 1;
    const maxX = datanum;
    // titleとxを除外
    const values = rows
      .slice(1)
      .map((row) => {
        return row.slice(1);
      })
      .flat() as number[];
    const rangeY = getYAxisRange(
      Array.from(new Set(values)),
      this.YAxisRangeCorrection,
      this.YAxisZeroValue
    );
    if (!this._c3) {
      this._c3 = C3.generate({
        bindto: chartid,
        size: {
          width: size.width,
          height: size.height,
        },
        data: { x: "x", rows: rows, type: "line" },
        axis: {
          x: { min: minX, max: maxX },
          y: { min: rangeY.min, max: rangeY.max },
        },
        point: {
          show: true,
        },
      });
      insertRangeElement(this._c3, size.height);
      insertZoomTarget(this._c3);
      this.addContextMenu(this.SvcTag);
    } else {
      const c3 = this._c3;
      c3.resize({ width: size.width, height: size.height });
      c3.axis.range({
        min: { x: minX, y: rangeY.min },
        max: { x: maxX, y: rangeY.max },
      });
      c3.load({ rows: rows });
      initRangeElement(c3, size.height);
      initZoomTarget(c3);
    }
    this.chartSize(size);
    this.initAxisRange(this._c3);
    displayChartPoint(this._c3, false);
    this.editChart(this._c3, false);
    setMousewheel(this._c3, { min: minX, max: maxX }, datanum);
  }

  /**
   * チャートの編集可不可状態を切り替えます
   * @param c3 - C3
   * @param edit - 編集可にする場合はtrue
   */
  editChart(c3: C3.ChartAPI, edit: boolean) {
    const c3i = c3.internal;
    const root = getRootElement(c3);
    if (edit) {
      const margin = c3i.margin;
      const onmouseup = this.handleChartMouseup;
      const dragInfo = this.dragInfo;
      const clickStart = this.clickStart;
      const setEditMenuXRange = this.setEditMenuXRange;
      root
        .on("mousedown", function () {
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          const hiddenIds = getHiddenIds(c3i);
          const circleArray = Array.from<CircleNode>(getCircles(c3i));
          const filter =
            hiddenIds.length > 0
              ? circleArray.filter((circle) => {
                  const find = hiddenIds.find((id) => {
                    return id === circle.__data__.id;
                  });
                  return typeof find === "undefined" ? true : false;
                })
              : circleArray;
          const circle = getCircle(filter.reverse(), mouseX, mouseY);
          if (circle) {
            const xrange = getRangeElement(c3);
            const sindex = parseInt(xrange.attr("start"));
            const eindex = parseInt(xrange.attr("end"));
            let start = circle.x;
            let end = start;
            if (eindex - sindex > 0) {
              start = sindex;
              end = eindex;
            }
            const { min, max } = c3.axis.range();
            const ranges = getYRanges(c3, start, end, circle.id, min.y, max.y);
            dragInfo({
              drag: true,
              circle: circle,
              start: start,
              end: end,
              ranges: ranges,
            });
          } else {
            const xs = getAxisXValues(c3);
            startRangeSelection(c3, xs, mouseX, setEditMenuXRange);
            dragInfo({ drag: true });
          }
          clickStart({ x: mouseX, y: mouseY });
          window.addEventListener("mouseup", onmouseup);
        })
        .on("mousemove", function () {
          const { drag, circle, start, end, ranges } = dragInfo();
          if (!drag) {
            return;
          }
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          if (circle) {
            if (ranges) {
              const { id, x } = circle;
              const circles = getCircles(c3i);
              const value = getValue(circles, id, x);
              if (typeof value !== "undefined") {
                const { min, max } = c3.axis.range();
                updateCirclesValue(
                  c3i,
                  circles,
                  id,
                  {
                    start: start < min.x ? min.x : start,
                    end: max.x < end ? max.x : end,
                  },
                  { min: roundYAxisValue(min.y), max: roundYAxisValue(max.y) },
                  ranges,
                  mouseY - c3i.y(value)
                );
                c3i.redraw({
                  withY: false,
                  withSubchart: false,
                  withTransition: false,
                  withTrimXDomain: false,
                  withEventRect: false,
                  withDimension: false,
                });
              }
            }
          } else {
            const xs = getAxisXValues(c3);
            setRangeSelection(c3, xs, mouseX, setEditMenuXRange);
          }
        })
        .on("mouseup", function () {
          const { x, y } = clickStart();
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          if (x === mouseX && y === mouseY) {
            setZoomTarget(c3, mouseX, mouseY);
          }
        })
        .on("click", null);
    } else {
      const margin = c3i.margin;
      root
        .on("mousedown", null)
        .on("mousemove", null)
        .on("mouseup", null)
        .on("click", function () {
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          setZoomTarget(c3, mouseX, mouseY);
        });
    }
  }

  render() {
    const { show, width, height } = this.props;
    if (this._c3) {
      resizeChart(this._c3, width, height, this.chartSize);
    }
    const xAxisRange = this.xAxisRange();
    return (
      <React.Fragment>
        <ChartDialog
          open={show}
          chartid={this.ChartID}
          onClose={this.handleClose}
          edit={this.state.edit}
          editMenu={true}
          onEdit={this.handleEdit}
          onSave={this.handleSave}
          onXZoomUp={this.handleXAxisZoomUp}
          onXZoomOut={this.handleXAxisZoomOut}
          onYZoomUp={this.handleYAxisZoomUp}
          onYZoomOut={this.handleYAxisZoomOut}
          id={{
            items: this.state.items,
          }}
          x={{
            start: this.state.start,
            end: this.state.end,
            onChange: this.handleChangeXRange,
          }}
          range={{
            x: { min: xAxisRange.min, max: xAxisRange.max },
          }}
          onRegist={this.handleRegist}
        />
      </React.Fragment>
    );
  }
}

/**
 * プロファイル編集ダイアログ
 */
export default withTranslation()(EditChartDialog);
